import * as angular from "angular";
import { CampaignDataProvider } from "./campaign.dp";
import { Effort } from "../models/effort.model";
import { EffortService } from "../services/effort.service";
import { effortsConstants } from "../utils/constants/efforts.constants";

export class EffortDataProvider {
    public readonly types = [...effortsConstants.effortTypes];
    private efforts: Effort[] = null;
    private selectedEffort: Effort;
    private getEffortsPromise: Promise<any> = null;

    private $q: any;
    private $timeout: any;

    private service: EffortService;
    private campaignDataProvider: CampaignDataProvider;

    constructor(
        $q: any,
        $timeout: any,
        EffortService: EffortService,
        CampaignDataProvider: CampaignDataProvider
    ) {
        this.$q = $q;
        this.$timeout = $timeout;

        this.service = EffortService;
        this.campaignDataProvider = CampaignDataProvider;

        this.selectedEffort = new Effort();
    }

    clearAll(): void {
        this.efforts = null;
        this.selectedEffort = new Effort();
        this.getEffortsPromise = null;
    }

    getSelectedEffort(active: boolean = false): Promise<Effort> {
        const deferred = this.$q.defer();
        if (!this.selectedEffort.id) {
            let effortid =
                this.campaignDataProvider.activeCampaign.getSelectedEffort();
            this.getActiveEfforts().then((efforts: Effort[]) => {
                if (!effortid) {
                    if (efforts.length > 0) {
                        effortid = efforts[0].id;
                    } else {
                        effortid = 1;
                    }
                } else {
                    let isEffortActive = false;
                    efforts.forEach((effort: Effort) => {
                        if (effortid == effort.id) {
                            isEffortActive = true;
                        }
                    });

                    if (!isEffortActive) {
                        if (efforts.length > 0) {
                            effortid = efforts[0].id;
                        } else {
                            effortid = 1;
                        }
                    }
                }
            });

            this.getEffortById(effortid, active).then((effort: Effort) => {
                this.setSelectedEffort(effort, false);
                deferred.resolve(this.selectedEffort);
            });
        } else {
            deferred.resolve(this.selectedEffort);
        }
        return deferred.promise;
    }

    setSelectedEffort(
        newSelectedEffort?: Effort,
        saveLocalStorage: boolean = true
    ): void {
        if (newSelectedEffort) {
            angular.extend(this.selectedEffort, newSelectedEffort);
            if (saveLocalStorage) {
                this.campaignDataProvider.activeCampaign.setSelectedEffort(
                    newSelectedEffort.id
                );
            }
        }
        if (!this.selectedEffort.id) {
            this.selectedEffort.id = "1";
        }
    }

    getEffortById(id: string, active: boolean): Promise<any> {
        const deferred = this.$q.defer();
        let effortPromise = active
            ? this.getActiveEfforts()
            : this.getAllEfforts();
        effortPromise.then((efforts: Effort[]) => {
            let filteredEffort = this.effortWithID(id, efforts);
            if (filteredEffort) {
                deferred.resolve(filteredEffort);
            } else {
                deferred.resolve(efforts[0]);
            }
        });

        return deferred.promise;
    }

    createEffort(effort: Effort): Promise<any> {
        const deferred = this.$q.defer();
        this.service.create(effort).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    if (!this.efforts) this.efforts = [];
                    this.replaceEffort(json.data);
                    deferred.resolve(json.data.attributes);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    updateEffort(effort: Effort): Promise<any> {
        const deferred = this.$q.defer();
        this.service.update(effort).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.replaceEffort(json.data);
                    deferred.resolve(json.data.attributes);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getActiveEfforts(): Promise<Effort[]> {
        const deferred = this.$q.defer();
        this.getAllEfforts().then((efforts: Effort[]) => {
            deferred.resolve(
                efforts.filter((effort: Effort) => effort.status === "Active")
            );
        });
        return deferred.promise;
    }

    getAllEfforts(): Promise<Effort[]> {
        const deferred = this.$q.defer();
        if (this.efforts !== null) {
            deferred.resolve(this.efforts);
            this.getEffortsPromise = deferred.promise;
            return this.getEffortsPromise;
        } else if (this.getEffortsPromise) {
            return this.getEffortsPromise;
        } else {
            this.service.all().then(
                (response: object) => {
                    const json = response["data"];
                    this.efforts = [];
                    for (let i = 0; i < json.data.length; i++) {
                        this.efforts.push(new Effort(json.data[i]));
                    }
                    deferred.resolve(this.efforts);
                },
                (error: object) => {
                    this.efforts = [];
                    deferred.reject(error);
                }
            );
            this.getEffortsPromise = deferred.promise;
            return this.getEffortsPromise;
        }
    }

    activeEfforts(): Effort[] {
        return this.efforts.filter(
            (effort: Effort) => effort.status == "Active"
        );
    }

    archiveEffort(effort: Effort): Promise<any> {
        const deferred = this.$q.defer();
        this.service.archive(effort).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json.data.attributes);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    replaceEffort(effortData: any): void {
        this.$timeout(() => {
            let existingField = this.effortWithID(effortData.id, this.efforts);
            if (existingField) {
                angular.extend(existingField, effortData.attributes);
            } else {
                let effort = new Effort(effortData);
                if (effort.status === "Active") {
                    this.efforts.unshift(effort);
                } else {
                    this.efforts.push(effort);
                }
            }
        });
    }

    effortWithID(id: string, efforts: Effort[]): Effort {
        for (let i = 0; i < efforts.length; i++) {
            if (efforts[i].id == id) {
                return efforts[i];
            }
        }
        return null;
    }

    processSocketUpdate(socketData: object): void {
        if (this.efforts) {
            this.replaceEffort(socketData);
        }
    }

    processSocketCreate(socketData: object): void {
        if (this.efforts) {
            this.replaceEffort(socketData);
        }
    }
}

angular
    .module("vcDataProvider")
    .service("EffortDataProvider", EffortDataProvider);
